<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="filterRequests"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            @click:row="openRequest"
            fixed-header
        >
            <!--HEADERS-->
            <template v-slot:[`header.number`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="idToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.items`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="itemsToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.approvedBy`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="approvedBy"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.deliveryLocation`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="locationToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.date`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="dateToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.dueDate`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="dueDateToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.notes`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="notesToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <template v-slot:[`header.createdBy`]="{ header }">
                <v-text-field
                    :label="header.text"
                    v-model="usersToFilter"
                    dense
                    class="pt-2"
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.approvedBy`]="{ item }">
                <p class="my-0 text-capitalize font-weight-medium">
                    {{ getUser(item.approvedBy) }}
                </p>
            </template>
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="my-0">
                    <div>
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ item.createdBy.name }}
                                </p>
                            </template>
                            <span>Creator</span>
                        </v-tooltip>
                    </div>
                    <div v-if="item.projectManager">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ item.projectManager.name }}
                                </p>
                            </template>
                            <span>Project Manager</span>
                        </v-tooltip>
                    </div>
                    <div v-if="item.buyer">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <p
                                    class="my-0 font-weight-medium"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    {{ getUser(item.buyer) }}
                                </p>
                            </template>
                            <span>Buyer</span>
                        </v-tooltip>
                    </div>
                </div>
            </template>
            <template v-slot:[`item.totalPrice`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ formatNumber(item.totalPrice) }}</p>
                </div>
            </template>
            <template v-slot:[`item.date`]="{ item }">
                <p class="my-0 text-capitalize">
                    {{ formatDate(item.createdOn.seconds) }}
                </p>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <p class="my-0 text-capitalize">{{ item.project.name }}</p>
            </template>
            <template v-slot:[`item.items`]="{ item }">
                <div>
                    <p
                        class="my-0"
                        v-for="(element, index) of item.items"
                        :key="index"
                    >
                        {{ element.description }} ({{ element.quantity }})
                    </p>
                </div>
            </template>
        </v-data-table>
        <!--ALERT MESSAGES-->
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--Request Form-->
        <v-dialog persistent fullscreen v-model="openForm">
            <PurchaseRequest
                v-if="openForm"
                :localRequest="selectedRequest"
                :originalRequest="originalRequest"
                :createForm="createForm"
                @closeDialog="closeRequestForm"
                @replaceRequest="replaceRequest"
            />
        </v-dialog>
        <!--Purchase Order-->
        <v-dialog persistent v-model="inTransitForm" max-width="600px">
            <PurchaseOrder
                v-if="inTransitForm"
                :request="selectedRequest"
                :originalRequest="originalRequest"
                @closeDialog="closeIntransitForm"
                @moveToInTransit="moveToInTransit"
            />
        </v-dialog>
    </div>
</template>

<script>
import PurchaseRequest from '@/components/Purchasing/PurchaseRequest.vue'
import PurchaseOrder from '@/components/Purchasing/PurchaseOrder.vue'
import _ from 'lodash'
import { auth, firestore } from '@/services/firebase'
import moment from 'moment'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import API from '@/services/api'

export default {
    name: 'PurchasingProcess',
    components: {
        PurchaseRequest,
        PurchaseOrder,
    },
    data: () => ({
        createForm: false,
        selectedRequest: {},
        originalRequest: {},
        openForm: false,
        activateAlert: false,
        alertMessage: '',
        loading: false,
        headers: [
            {
                text: 'ID',
                value: 'number',
                sortable: false,
                align: 'center',
                width: '100px',
            },
            {
                text: 'PROJECT',
                value: 'project',
                sortable: false,
                align: 'left',
            },
            {
                text: 'ITEMS',
                value: 'items',
                sortable: false,
                align: 'left',
            },
            {
                text: 'REQ DATE',
                value: 'date',
                sortable: false,
                align: 'center',
            },
            {
                text: 'DUE DATE',
                value: 'dueDate',
                sortable: false,
                align: 'center',
            },
            {
                text: 'APPROVED BY',
                value: 'approvedBy',
                sortable: false,
                align: 'center',
            },
            {
                text: 'USERS',
                value: 'createdBy',
                sortable: false,
                align: 'center',
            },
            {
                text: 'TOTAL PRICE',
                value: 'totalPrice',
                sortable: false,
                align: 'center',
            },

            {
                text: 'DELIVERY AT',
                value: 'deliveryLocation',
                sortable: false,
                align: 'left',
            },
            {
                text: 'NOTES',
                value: 'notes',
                sortable: false,
                align: 'left',
            },
        ],
        requests: [],
        approvedBy: undefined,
        projectToFilter: undefined,
        dateToFilter: undefined,
        dueDateToFilter: undefined,
        notesToFilter: undefined,
        usersToFilter: undefined,
        locationToFilter: undefined,
        deliveryParamToFilter: undefined,
        itemsToFilter: undefined,
        inTransitForm: false,
        listener: null,
        users: [],
        companyId: JSON.parse(localStorage.getItem('company')),
        idToFilter: undefined,
        resourceId: undefined,
    }),
    computed: {
        ...mapState(['notificationResource']),
        filterRequests() {
            this.openResource()
            let conditions = []

            if (this.approvedBy) {
                conditions.push(this.filterForApprovedBy)
            }

            if (this.itemsToFilter) {
                conditions.push(this.filterForItems)
            }

            if (this.projectToFilter) {
                conditions.push(this.filterForProject)
            }

            if (this.deliveryParamToFilter) {
                conditions.push(this.filterForDeliveryAt)
            }

            if (this.idToFilter) {
                conditions.push(this.filterForId)
            }

            if (this.dateToFilter) {
                conditions.push(this.filterForDate)
            }

            if (this.dueDateToFilter) {
                conditions.push(this.filterForDueDate)
            }

            if (this.locationToFilter) {
                conditions.push(this.filterForLocation)
            }

            if (this.notesToFilter) {
                conditions.push(this.filterForNotes)
            }

            if (this.usersToFilter) {
                conditions.push(this.filterForUsers)
            }

            if (conditions.length > 0) {
                return this.requests.filter(request => {
                    return conditions.every(condition => {
                        return condition(request)
                    })
                })
            }
            this.sort()
            return this.requests
        },
    },
    async created() {
        try {
            this.loading = true
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
    },
    async mounted() {
        try {
            this.resourceId = this.$route.query.resourceId
            const userId = auth().currentUser.uid
            this.loading = true
            let PurchasesQuery = firestore
                .collection('companies')
                .doc(this.companyId)
                .collection('purchases')
                .where('status', '==', 'purchasing')
                .where('buyer', '==', userId)

            this.listener = await PurchasesQuery.onSnapshot(
                async docSnapshot => {
                    docSnapshot.docChanges().forEach(async change => {
                        const request = Object.assign(change.doc.data(), {
                            id: change.doc.id,
                        })
                        if (request.items) {
                            request.totalPrice = 0

                            request.items.forEach(item => {
                                let quoteAble = item.quotes.find(
                                    quote => quote.able
                                )
                                request.totalPrice +=
                                    Number(item.quantity || 0) *
                                    Number(
                                        quoteAble.priceBeforeTRM ??
                                            quoteAble.price ??
                                            0
                                    )

                                request.itemsLabel = request.itemsLabel
                                    ? request.itemsLabel +
                                      `${item.description} ${
                                          item.purchaseOrder
                                              ? '(' +
                                                item.purchaseOrder.orderNumber +
                                                ')'
                                              : ''
                                      }`
                                    : `${item.description} ${
                                          item.purchaseOrder
                                              ? '(' +
                                                item.purchaseOrder.orderNumber +
                                                ')'
                                              : ''
                                      }`
                            })
                        }
                        if (change.type === 'added') {
                            request.dueDate = this.formatDate(
                                request.dueDate.seconds
                            )
                            this.requests.push(request)
                        }
                        if (change.type === 'modified') {
                            request.dueDate = this.formatDate(
                                request.dueDate.seconds
                            )
                            const index = this.requests.findIndex(
                                r => r.id == request.id
                            )
                            if (index >= 0) {
                                this.requests.splice(index, 1, request)
                            }
                        }
                        if (change.type === 'removed') {
                            const index = this.requests.findIndex(
                                r => r.id == request.id
                            )
                            if (index >= 0) {
                                this.requests.splice(index, 1)
                            }
                        }
                    })
                }
            )
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        this.listener()
        this.listener = null
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        formatNumber(number) {
            return new Intl.NumberFormat('de-DE').format(
                Math.ceil(number != undefined ? number : 0)
            )
        },
        getUser(id) {
            const user = this.users.find(u => u.id == id)
            if (user) {
                return user.name
            }
            return null
        },
        filterForItems(item) {
            return (
                item.itemsLabel &&
                item.itemsLabel
                    .toLowerCase()
                    .includes(this.itemsToFilter.toLowerCase())
            )
        },
        filterForId(item) {
            return item.number.toString().includes(this.idToFilter)
        },
        formatDate(seconds) {
            return moment.unix(seconds).format('YYYY-MM-DD')
        },
        openIntransitForm(request) {
            this.selectedRequest = _.cloneDeep(request)
            this.originalRequest = _.cloneDeep(request)
            this.inTransitForm = true
        },
        closeIntransitForm() {
            this.selectedRequest = {}
            this.originalRequest = {}
            this.inTransitForm = false
        },
        filterForDeliveryAt(item) {
            return (
                item.deliveryAt &&
                item.deliveryAt
                    .toLowerCase()
                    .includes(this.deliveryParamToFilter.toLowerCase())
            )
        },
        filterForProject(item) {
            return (
                item.project &&
                item.project.name
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase())
            )
        },
        filterForApprovedBy(item) {
            return (
                item.approvedBy &&
                item.approvedBy.name
                    .toLowerCase()
                    .includes(this.approvedBy.toLowerCase())
            )
        },
        filterForDate(item) {
            const date = this.formatDate(
                item.createdOn.seconds
                    ? item.createdOn.seconds
                    : item.createdOn._seconds
            )
            return date.includes(this.dateToFilter)
        },
        filterForDueDate(item) {
            return item.dueDate.includes(this.dueDateToFilter)
        },
        filterForNotes(item) {
            return (
                item.notes &&
                item.notes
                    .toLowerCase()
                    .includes(this.notesToFilter.toLowerCase())
            )
        },
        filterForLocation(item) {
            return (
                item.deliveryLocation &&
                item.deliveryLocation
                    .toLowerCase()
                    .includes(this.locationToFilter.toLowerCase())
            )
        },
        filterForUsers(item) {
            return (
                (item.createdBy.name &&
                    item.createdBy.name
                        .toLowerCase()
                        .includes(this.usersToFilter.toLowerCase())) ||
                (item.projectManager.name &&
                    item.projectManager.name
                        .toLowerCase()
                        .includes(this.usersToFilter.toLowerCase())) ||
                (item.buyer &&
                this.users.find(user => user.id == item.buyer) != undefined
                    ? this.users
                          .find(user => user.id == item.buyer)
                          .name.toLowerCase()
                          .includes(this.usersToFilter.toLowerCase())
                    : false)
            )
        },
        openRequest(request) {
            this.selectedRequest = _.cloneDeep(request)
            this.originalRequest = _.cloneDeep(request)
            this.openForm = true
        },
        closeRequestForm() {
            this.openForm = false
            this.selectedRequest = {}
            this.originalRequest = {}
        },
        async moveToInTransit(item) {
            try {
                if (this.validatePurchaseOrder(item)) {
                    this.alertMessage =
                        'You must upload purchase orders to continue.'
                    this.activateAlert = true
                    return null
                }
                await API.updatePurchaseRequest(item.id, {
                    status: 'inTransit',
                })
                this.alertMessage =
                    'Purchase was successfully changed to In Transit'
                this.activateAlert = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        validatePurchaseOrder(request) {
            let valPurchaseOrder = false
            for (const item of request.items) {
                if (!item.purchaseOrder || !item.purchaseOrder.orderNumber) {
                    valPurchaseOrder = true
                    break
                }
            }
            return !!valPurchaseOrder
        },
        replaceRequest() {},
        openResource() {
            if (this.resourceId) {
                const resource = this.requests.find(
                    r => r.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    this.openRequest(resource)
                }
            }
        },
        sort() {
            this.requests.sort(
                (a, b) => b.createdOn.seconds - a.createdOn.seconds
            )
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
    position: sticky;
    z-index: 1;
}
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
.v-data-table__wrapper {
    max-height: 72vh;
    overflow-y: auto;
}
.v-data-table__row {
    height: 64px;
}
.v-data-table__wrapper thead tr {
    position: sticky;
    top: 0;
    z-index: 10;
}
</style>
